import React from 'react';

/**
 * Renders markdown-like text with proper formatting
 * Handles headers, lists, bold, tables, and line breaks
 */
export function FormattedContent({ content, className = "" }) {
  if (!content) return null;
  
  // Process the content line by line
  const lines = content.split('\n');
  const elements = [];
  let currentList = [];
  let listType = null;
  let tableRows = [];
  let tableHeaders = [];
  let inTable = false;
  
  const flushList = () => {
    if (currentList.length > 0) {
      if (listType === 'ul') {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-3 ml-4">
            {currentList.map((item, i) => (
              <li key={i} className="text-slate-700">{processInlineFormatting(item)}</li>
            ))}
          </ul>
        );
      } else if (listType === 'ol') {
        elements.push(
          <ol key={`list-${elements.length}`} className="list-decimal list-inside space-y-1 my-3 ml-4">
            {currentList.map((item, i) => (
              <li key={i} className="text-slate-700">{processInlineFormatting(item)}</li>
            ))}
          </ol>
        );
      }
      currentList = [];
      listType = null;
    }
  };

  const flushTable = () => {
    if (tableHeaders.length > 0 || tableRows.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="my-4 overflow-x-auto">
          <table className="min-w-full border-collapse border border-slate-200 rounded-lg overflow-hidden">
            {tableHeaders.length > 0 && (
              <thead className="bg-slate-50">
                <tr>
                  {tableHeaders.map((header, i) => (
                    <th key={i} className="px-4 py-3 text-left text-sm font-semibold text-slate-900 border-b border-slate-200">
                      {processInlineFormatting(header.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody className="divide-y divide-slate-200">
              {tableRows.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-4 py-3 text-sm text-slate-700 border-slate-200">
                      {processInlineFormatting(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableHeaders = [];
      tableRows = [];
      inTable = false;
    }
  };
  
  const processInlineFormatting = (text) => {
    if (!text) return text;
    
    // Handle bold **text** or __text__
    const parts = [];
    let remaining = text;
    let key = 0;
    
    while (remaining) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
      if (boldMatch) {
        const beforeBold = remaining.substring(0, boldMatch.index);
        if (beforeBold) {
          parts.push(<span key={key++}>{beforeBold}</span>);
        }
        parts.push(<strong key={key++} className="font-semibold text-slate-900">{boldMatch[1] || boldMatch[2]}</strong>);
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
      } else {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Check if a line is a table separator (e.g., |---|---|---|)
  const isTableSeparator = (line) => {
    return /^\|[\s\-:]+\|/.test(line.trim());
  };

  // Check if a line is a table row
  const isTableRow = (line) => {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|');
  };

  // Parse a table row into cells
  const parseTableRow = (line) => {
    const trimmed = line.trim();
    // Remove leading and trailing pipes, then split by |
    return trimmed.slice(1, -1).split('|').map(cell => cell.trim());
  };
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Handle table rows
    if (isTableRow(trimmedLine)) {
      if (isTableSeparator(trimmedLine)) {
        // This is the separator row, skip it but mark we're in a table
        inTable = true;
        return;
      }
      
      const cells = parseTableRow(trimmedLine);
      
      if (!inTable && tableHeaders.length === 0) {
        // First row is headers
        tableHeaders = cells;
      } else {
        // Data row
        tableRows.push(cells);
      }
      return;
    } else if (inTable || tableHeaders.length > 0) {
      // End of table, flush it
      flushTable();
    }
    
    // Skip empty lines but add spacing
    if (!trimmedLine) {
      flushList();
      elements.push(<div key={`space-${index}`} className="h-2" />);
      return;
    }
    
    // H1 headers (# Header or === underline)
    if (trimmedLine.startsWith('# ')) {
      flushList();
      elements.push(
        <h1 key={`h1-${index}`} className="text-2xl font-bold text-slate-900 mt-6 mb-3 pb-2 border-b border-slate-200">
          {processInlineFormatting(trimmedLine.substring(2))}
        </h1>
      );
      return;
    }
    
    // H2 headers (## Header)
    if (trimmedLine.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={`h2-${index}`} className="text-xl font-semibold text-slate-800 mt-5 mb-2">
          {processInlineFormatting(trimmedLine.substring(3))}
        </h2>
      );
      return;
    }
    
    // H3 headers (### Header)
    if (trimmedLine.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={`h3-${index}`} className="text-lg font-semibold text-slate-800 mt-4 mb-2">
          {processInlineFormatting(trimmedLine.substring(4))}
        </h3>
      );
      return;
    }
    
    // H4 headers (#### Header)
    if (trimmedLine.startsWith('#### ')) {
      flushList();
      elements.push(
        <h4 key={`h4-${index}`} className="text-base font-semibold text-slate-700 mt-3 mb-1">
          {processInlineFormatting(trimmedLine.substring(5))}
        </h4>
      );
      return;
    }
    
    // Unordered list items (- item or * item)
    if (trimmedLine.match(/^[-*]\s+/)) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(trimmedLine.replace(/^[-*]\s+/, ''));
      return;
    }
    
    // Ordered list items (1. item)
    if (trimmedLine.match(/^\d+\.\s+/)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(trimmedLine.replace(/^\d+\.\s+/, ''));
      return;
    }
    
    // Horizontal rule (--- or ***)
    if (trimmedLine.match(/^(---+|\*\*\*+)$/)) {
      flushList();
      elements.push(<hr key={`hr-${index}`} className="my-4 border-slate-200" />);
      return;
    }
    
    // Blockquote (> text)
    if (trimmedLine.startsWith('> ')) {
      flushList();
      elements.push(
        <blockquote key={`quote-${index}`} className="pl-4 border-l-4 border-sky-300 my-3 text-slate-600 italic bg-slate-50 py-2 pr-4 rounded-r">
          {processInlineFormatting(trimmedLine.substring(2))}
        </blockquote>
      );
      return;
    }

    // Code block (```code```)
    if (trimmedLine.startsWith('```')) {
      flushList();
      // Simple inline code handling
      return;
    }

    // Inline code (`code`)
    if (trimmedLine.includes('`')) {
      flushList();
      const parts = [];
      let remaining = trimmedLine;
      let key = 0;
      
      while (remaining) {
        const codeMatch = remaining.match(/`([^`]+)`/);
        if (codeMatch) {
          const beforeCode = remaining.substring(0, codeMatch.index);
          if (beforeCode) {
            parts.push(<span key={key++}>{processInlineFormatting(beforeCode)}</span>);
          }
          parts.push(
            <code key={key++} className="px-1.5 py-0.5 bg-slate-100 text-slate-800 rounded text-sm font-mono">
              {codeMatch[1]}
            </code>
          );
          remaining = remaining.substring(codeMatch.index + codeMatch[0].length);
        } else {
          parts.push(<span key={key++}>{processInlineFormatting(remaining)}</span>);
          break;
        }
      }
      
      elements.push(
        <p key={`p-${index}`} className="text-slate-700 my-2 leading-relaxed">
          {parts}
        </p>
      );
      return;
    }
    
    // Regular paragraph
    flushList();
    elements.push(
      <p key={`p-${index}`} className="text-slate-700 my-2 leading-relaxed">
        {processInlineFormatting(trimmedLine)}
      </p>
    );
  });
  
  // Flush any remaining list or table
  flushList();
  flushTable();
  
  return (
    <div className={`formatted-content ${className}`}>
      {elements}
    </div>
  );
}

export default FormattedContent;
